import { useEffect, useRef, useState } from 'react';
import { t, type Lang } from './i18n';
import { bewussteAkzentfarbe } from '@scrolly/post-types';
import type { StartButtonStyle, StartTypography } from '@scrolly/post-types';

export type CoverTextPosition = 'bottom-left' | 'bottom-center' | 'center';

export interface CoverSectionProps {
  title: string;
  description?: string;
  eyebrowText?: string;
  coverImage?: string;
  onStart: () => void;
  progress?: number;
  isOgMode?: boolean;
  height: number;
  lang?: Lang;
  /** Text im Start-Button (Default: lokalisiertes „START"). */
  startButtonText?: string;
  /**
   * GESTALTUNG des Start-Knopfs aus dem Pro-Editor-Startbildschirm
   * (Radius, Füllung, Rand, Akzent — Jans Fund 24.08.2026: die Felder
   * wurden nie gespiegelt, Vorschau/Leser zeigten den Standard-Knopf).
   * undefined/null = Standard-Design exakt wie bisher.
   */
  startButtonStyle?: StartButtonStyle | null;
  /**
   * Bild, das den Start-Button ERSETZT (Adresse; leer = normaler Knopf).
   *
   * Gepflegt wird es im Startbildschirm-Menü des Pro-Editors („Ein
   * importiertes Asset ersetzt den START BUTTON"). Bis dahin kannte nur der
   * Pro-Editor dieses Bild — Vorschau und Leser zeigten weiter den Textknopf
   * (Jans Fund 05.08.2026). Die Spiegelung in die Metadaten macht cover-meta.
   */
  startButtonImage?: string;
  /**
   * BOOT-BLENDE (Jans Auftrag 02.09.2026, Benedikts „Deckel bleibt zu lange
   * schwarz"): Titelbild, das NUR waehrend des Renderer-Boots den schwarzen
   * Canvas deckt und bei progress>=1 weich zum Live-Slide ueberblendet.
   * Praezisierung der Regel vom 24.08. („beim Titel immer nur der Vorhang"):
   * Die gilt weiter fuer den eingeschwungenen Zustand — waehrend des Boots
   * steht sonst sekundenlang Schwarz, obwohl das Cover-Webp laengst da ist
   * (gemessen 02.09.: Bild nach 0,6-1,9 s, Canvas-Boot je nach Netz/CPU
   * 2-14 s). Leer/undefined = Verhalten exakt wie bisher.
   */
  bootImage?: string;
  /** Anordnung des Titel-Blocks auf dem Cover. */
  position?: CoverTextPosition;
  /** Intro-Vorhang anzeigen (Overlay über dem Slide-1-Bild). Default: true. */
  showCurtain?: boolean;
  /** Slide-1-Bild unter dem Vorhang unscharf. */
  curtainBlur?: boolean;
  /** Vorhang-Tint: dunkel (Default) oder hell. */
  curtainTheme?: 'dark' | 'light';
  /** Branding-Headline-Font (für die Überschrift). */
  headlineFont?: string;
  /** Branding-Text-Font (für Topline + Untertitel). */
  textFont?: string;
  /**
   * Per-Scrolly-Typografie aus dem Pro-Editor-Startbildschirm (Schrift +
   * Größenfaktor für Titel-/Unterzeile). Schlägt das Branding — der Autor
   * hat das letzte Wort. Auflösung: resolveStartTypography (post-types).
   */
  startTypography?: StartTypography | null;
  /** Sentence-Bloom: no subtitle on cover; solid black when no hero image. */
  isSentenceBloom?: boolean;
  /**
   * „Aufladen + einrasten"-Animation für den Start-Button (Default: false).
   * NUR der Pre-Mount-Cover (public Phase 1, vor dem Klick) setzt true: der
   * Button ist sichtbar, lädt mit `progress` auf und wird erst bei voller Ladung
   * klickbar. Alle anderen (Phase-2-renderCover nach dem Klick, Editor-Previews,
   * Mock) lassen es false → klassisches Verhalten (Button ab progress>=1, davor
   * Ladeanzeige). Sonst liefe die Aufladen-Animation nach dem Start ein zweites
   * Mal (Bene 2026-08-14: „nach dem Klick kommt die Animation noch einmal").
   */
  animateLoading?: boolean;
}

const POSITION_STYLE: Record<
  CoverTextPosition,
  {
    justify: 'flex-end' | 'center';
    align: 'flex-start' | 'center';
    textAlign: 'left' | 'center';
  }
> = {
  'bottom-left': {
    justify: 'flex-end',
    align: 'flex-start',
    textAlign: 'left',
  },
  'bottom-center': {
    justify: 'flex-end',
    align: 'center',
    textAlign: 'center',
  },
  center: { justify: 'center', align: 'center', textAlign: 'center' },
};

export function CoverSection({
  title,
  description,
  eyebrowText,
  coverImage,
  onStart,
  progress = 1,
  isOgMode = false,
  height,
  lang = 'de',
  startButtonText,
  startButtonStyle,
  startTypography,
  startButtonImage,
  bootImage,
  position = 'bottom-left',
  showCurtain = true,
  curtainBlur = false,
  curtainTheme = 'dark',
  headlineFont,
  textFont,
  isSentenceBloom = false,
  // animateLoading steuert seit dem Einheits-Knopf (26.08.2026) nichts mehr —
  // Prop bleibt fuer bestehende Aufrufer, wird aber bewusst ignoriert.
  animateLoading: _animateLoading = false,
}: CoverSectionProps) {
  // RESPONSIVER TITEL (Jans Wunsch 25.08.2026): Der Editor skaliert den
  // Startbildschirm-Titel laengst mit Breite + Schmal-Ausgleich
  // (StartScreenOverlay: basis = w*(0.06 + narrow*0.05), min 18) — der Leser
  // stand fest auf 34 px, auf 5K also winzig. Hier dieselbe Wachstums-Kurve,
  // verankert am bisherigen Design: bei typischer Phone-Breite ergibt die
  // Formel ~34 px, dort aendert sich also nichts; auf grossen Flaechen
  // waechst der ganze Titelblock (Schriften, Abstaende, Blockbreite) mit.
  const sectionRef = useRef<HTMLElement | null>(null);
  // RESPONSIVE TITEL-GROESSE — seit 02.09.2026 in REINEM CSS (Jans Fund:
  // "Titel springt zwischendurch hoch"): Die fruehere JS-Messung
  // (ResizeObserver -> skala) wurde erst nach der Hydration wirksam — seit
  // das Cover aus dem Server-HTML sofort sichtbar ist, stand der Titel
  // sekundenlang in Basisgroesse und schnappte dann um. Die 25.08.-Formel
  // (basis = w*(0.06 + narrow*0.05), min 18px) ist hier als BREITEN-only-
  // Naeherung in Container-Einheiten uebersetzt — die Hoehe darf nicht in
  // die Formel (sie wird per JS nachgemessen und wuerde springen). Fit:
  // 375er Handy exakt (32.6), 1200er Desktop exakt (72), Tablet -6 %:
  //     basis = max(18px, min(8.7cqw, 18px + 4.5cqw))
  // Damit rendert schon das Server-HTML die Endgroesse — kein Sprung.
  //
  // QUERFORMAT (Jans Kundenfund 28.09.2026, alphabeta „LUMA"): Die Formel
  // kannte nur die BREITE — ein Laptop im Vollbild (1860 × 1000) bekam den
  // Titel 2,4-fach und den START-Knopf 3,2-fach so gross wie im Handy-Rahmen,
  // obwohl er nicht hoeher ist. Deshalb zaehlt jetzt hoechstens eine
  // „Ersatzbreite" von 0,8 × Bildschirmhoehe (svh = kleinste sichtbare Hoehe,
  // springt nicht mit der Adressleiste). Hochformat (Handy, Handy-Rahmen,
  // Tablet hochkant) ist davon unberuehrt: dort ist die Breite ohnehin kleiner.
  const COVER_BASIS =
    'max(18px, min(8.7cqw, 18px + 4.5cqw, 6.96svh, 18px + 3.6svh))';
  const basisMal = (faktor: number) =>
    `calc((${COVER_BASIS}) * ${faktor.toFixed(4)})`;
  // TITEL UND UNTERZEILE: AUF GROSSEN FLAECHEN KLEINER, AM HANDY WIE BISHER
  // (Jan 17.09.2026). Erst pauschal x2/3 — das machte die Schrift am
  // Smartphone zu klein („auf dem Smartphone war vorher alles gut, nur nicht
  // bei den ganz grossen Screens"). Jetzt eine zweite, flachere Kurve, die die
  // Basis nur DECKELT:
  //     textBasis = min(basis, 26px + 2.25cqw)
  //   390 px (Handy):   33,9 px → 33,9 px  (unveraendert)
  //   768 px (Tablet):  52,6 px → 43,3 px
  //   1024 px:          64,1 px → 49,0 px
  //   1900 px:         103,5 px → 68,8 px  (= 2/3)
  // Gilt nur fuer die beiden Textgroessen — START-Knopf, Logo, Abstaende und
  // Blockbreite haengen weiter an der unveraenderten Basis. Die Typografie-
  // Regler je Scrolly (headlineScale/sublineScale) wirken weiter.
  const COVER_TEXT_BASIS = `min(${COVER_BASIS}, 26px + 2.25cqw)`;
  const textBasisMal = (faktor: number) =>
    `calc((${COVER_TEXT_BASIS}) * ${faktor.toFixed(4)})`;
  const hasDescription =
    !isSentenceBloom &&
    typeof description === 'string' &&
    description.length > 0 &&
    description !== title;

  // Start-Button. Bei animateLoading (nur public Phase 1, vor dem Klick):
  // „Aufladen + einrasten" — der Button ist die ganze Zeit sichtbar, lädt mit
  // `progress` SICHTBAR auf und wird erst bei voller Ladung klickbar, damit ein
  // Klick immer sofort „funktioniert" (Bene-Wunsch). Sonst klassisch: Button
  // erst ab progress>=1, davor Ladeanzeige — so läuft die Aufladen-Animation
  // NICHT ein zweites Mal, wenn nach dem Klick der Renderer-renderCover erneut
  // eine CoverSection mit 0→1-progress zeigt (Bene 2026-08-14).
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const isReady = clampedProgress >= 1;

  // ANZEIGE-Fortschritt (Jans Versuch 02.09.2026, „Balken springt nur am
  // Ende"): Der echte Fortschritt zaehlt fertige EBENEN — auf schmalem Netz
  // laden alle parallel und werden fast gleichzeitig fertig, davor steht der
  // Balken still. Fuer die Anzeige rieselt der Balken deshalb langsam weiter
  // (Deckel 90 %), wird vom echten Fortschritt jederzeit ueberholt und
  // luegt nie „fertig" — Freigabe (isReady) haengt WEITER am echten Wert.
  const [anzeigeProgress, setAnzeigeProgress] = useState(0);
  const rieselStart = useRef<number | null>(null);
  useEffect(() => {
    if (isReady) {
      setAnzeigeProgress(1);
      return undefined;
    }
    if (rieselStart.current === null) rieselStart.current = Date.now();
    const takt = setInterval(() => {
      // Auslauf-Kurve (schnell los, asymptotisch gegen 85 %): nach 2 s ~19 %,
      // nach 5 s ~40 %, nach 10 s ~61 %, nach 20 s ~78 % — der Balken lebt
      // von Sekunde 1 an, ohne je "fertig" zu behaupten.
      const t = Date.now() - (rieselStart.current ?? Date.now());
      const kurve = 0.85 * (1 - Math.exp(-t / 8000));
      setAnzeigeProgress((a) => Math.max(a, kurve, clampedProgress));
    }, 150);
    return () => clearInterval(takt);
  }, [isReady, clampedProgress]);
  // KLICK-VORMERKUNG (Jans Fund 26.08.2026): Bei gestalteten Knoepfen war
  // die Auflade-Fuellung fast unsichtbar — der Knopf sah scharf aus, war
  // aber noch disabled, fruehe Klicks verpufften wortlos ("START
  // funktioniert nicht so richtig", Safari/dev mit kalten Caches). Jetzt:
  // ein Klick vor voller Ladung wird gemerkt und startet AUTOMATISCH,
  // sobald alles geladen ist (Benes Grundsatz: ein Klick wirkt immer).
  const [startVorgemerkt, setStartVorgemerkt] = useState(false);
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  useEffect(() => {
    if (isReady && startVorgemerkt) {
      setStartVorgemerkt(false);
      onStartRef.current?.();
    }
  }, [isReady, startVorgemerkt]);
  const startKlick = () => {
    if (isReady) {
      onStart?.();
    } else {
      setStartVorgemerkt(true);
    }
  };
  // Knopf IMMER sofort zeigen (Jans Vorgabe 26.08.2026: in den Previews
  // klappte er erst nach ~3 s Boot nach). Laden = dezentes Dimmen; Klicks
  // davor werden vorgemerkt.
  const showButton = !isOgMode;
  const buttonLabel =
    typeof startButtonText === 'string' && startButtonText.trim().length > 0
      ? startButtonText
      : t('cover.start', lang);

  // Gestalteter Knopf (Pro-Editor): Pro-Editor-Vorgaben als Rückfall
  // (Radius 100, Rand 2, Füllung transparent, Rand = Akzent), Akzent-
  // Rückfall = Weiß (Vorhang ist dunkel). OHNE Gestaltung bleibt alles
  // exakt beim bisherigen Pink-Design — ebenso mit `standard` (Schalter
  // „Standard-Button (Pill-Farbe)“): dann färbt die Akzentfarbe nur die Texte.
  const gestaltet = Boolean(
    startButtonStyle &&
      !startButtonStyle.standard &&
      Object.keys(startButtonStyle).length > 0,
  );
  const stilAkzent = startButtonStyle?.accentColor || '#ffffff';
  const stilStyles: React.CSSProperties | undefined = gestaltet
    ? {
        background: startButtonStyle?.fillColor || 'transparent',
        color: stilAkzent,
        border: `${startButtonStyle?.borderWidth ?? 2}px solid ${
          startButtonStyle?.borderColor || stilAkzent
        }`,
        borderRadius: startButtonStyle?.radius ?? 100,
      }
    : undefined;

  const [coverImgFailed, setCoverImgFailed] = useState(false);
  const showCoverImage = Boolean(coverImage) && !coverImgFailed && !isOgMode;

  // Boot-Blende (s. Prop-Doku): nur solange der Boot laeuft. Ein dauerhaftes
  // coverImage (Sentence-Bloom) hat Vorrang — dann braucht es keine Blende.
  const [bootBildFehler, setBootBildFehler] = useState(false);
  const bootBildAktiv =
    Boolean(bootImage) && !bootBildFehler && !isOgMode && !showCoverImage;

  const pos = POSITION_STYLE[position] ?? POSITION_STYLE['bottom-left'];
  // Schrift-Vorrang: Per-Scrolly-Typografie (Startbildschirm des Pro-Editors)
  // schlägt die Branding-Schrift, die den Theme-Fallback schlägt.
  const eigeneHeadlineFont = startTypography?.headlineFont || headlineFont;
  const eigeneSublineFont = startTypography?.sublineFont || textFont;
  // Per-Scrolly-Schrift gesetzt (Typografie am Startbildschirm des Pro-
  // Editors)? Dann folgen Gewicht/Laufweite/Zeilenhoehe dem Editor (s. h1).
  const eigeneSchrift =
    typeof startTypography?.headlineFont === 'string' &&
    startTypography.headlineFont.trim().length > 0;
  const headlineFamily =
    typeof eigeneHeadlineFont === 'string' && eigeneHeadlineFont.trim().length > 0
      ? `'${eigeneHeadlineFont}', var(--scrolly-font-display, 'Playfair Display', Georgia, serif)`
      : "var(--scrolly-font-display, 'Playfair Display', Georgia, serif)";
  const textFamily =
    typeof textFont === 'string' && textFont.trim().length > 0
      ? `'${textFont}', var(--scrolly-font-ui, 'DM Sans', system-ui, sans-serif)`
      : "var(--scrolly-font-ui, 'DM Sans', system-ui, sans-serif)";
  // Unterzeile darf eine EIGENE Familie tragen (Topline bleibt Branding).
  const sublineFamily =
    typeof eigeneSublineFont === 'string' && eigeneSublineFont.trim().length > 0
      ? `'${eigeneSublineFont}', var(--scrolly-font-ui, 'DM Sans', system-ui, sans-serif)`
      : textFamily;
  // Größenfaktoren multiplizieren auf die responsive Grundskala (#459).
  const headlineFaktor = startTypography?.headlineScale ?? 1;
  const sublineFaktor = startTypography?.sublineScale ?? 1;

  // Intro-Vorhang: Overlay-Verlauf über dem Slide-1-Bild. Dunkel (Default) oder
  // hell; Text-Farbe passt sich für Kontrast an. „Anzeigen" = showCurtain.
  const isLightCurtain = curtainTheme === 'light';
  const curtainGradient = isLightCurtain
    ? 'linear-gradient(to bottom, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.5) 55%, rgba(255,255,255,0.85) 100%)'
    : 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.86) 100%)';
  // „Text & Button"-Farbe des Pro-Editor-Startbildschirms: faerbt dort
  // Topline, Ueberschrift, Unterzeile UND Knopf — hier kam sie bisher nur am
  // Knopf an, Titel und Unterzeile blieben weiss (Jans Fund 19.09.2026).
  // Reines Weiss ist die Editor-VORGABE (nie bewusst gewaehlt) → dann bleibt
  // es bei der Vorhang-Logik, damit helle Vorhaenge ihren dunklen Text behalten.
  const textAkzent = bewussteAkzentfarbe(startButtonStyle?.accentColor);
  const titleColor = textAkzent ?? (isLightCurtain ? '#1a1a1e' : '#fff');
  const descColor =
    textAkzent ??
    (isLightCurtain ? 'rgba(20,20,25,0.72)' : 'rgba(255,255,255,0.82)');
  const titleShadow = isLightCurtain ? 'none' : '0 2px 20px rgba(0,0,0,0.5)';

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      data-testid="cover-section"
      role={!isOgMode ? 'dialog' : undefined}
      aria-label={!isOgMode ? 'Story-Start-Gate' : undefined}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: `${height}px`,
        maxHeight: '100%',
        // Groessen-Container NUR ueber die Breite (inline-size): die Hoehe
        // des Rahmens wird per JS nachgemessen und wuerde die Schrift doch
        // wieder springen lassen — die Breite steht vom ersten Paint an fest.
        containerType: 'inline-size',
        zIndex: 1002,
        // Kein eigenes Cover-Bild: der Vorhang liegt transparent über der
        // gerenderten ersten Slide (Canvas dahinter). Nur wenn explizit ein
        // coverImage übergeben wird (z.B. Sentence-Bloom), dient es als Backdrop.
        background: 'transparent',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: pos.justify,
        alignItems: pos.align,
        // WESER-KURIER (2026-08-11, Laura „Startansicht angeschnitten"): in
        // nativen App-WebViews und auf Notch-Geräten verdeckte die obere/untere
        // System-/App-Leiste den Cover-Inhalt, weil das Top-/Bottom-Padding fix
        // war. env(safe-area-inset-*) schiebt Titel/CTA in den sichtbaren
        // Bereich; ohne Safe-Area (Desktop/normaler Browser) ist der Zusatz 0px
        // → unverändert. Analog zur TopBar (ViewerChrome), die die Safe-Area
        // schon respektiert.
        padding:
          'calc(32px + env(safe-area-inset-top, 0px)) 24px calc(40px + env(safe-area-inset-bottom, 0px))',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transition: 'opacity 0.6s ease-out',
      }}
    >
      {showCoverImage && (
        <img
          src={coverImage}
          alt=""
          aria-hidden="true"
          onError={() => setCoverImgFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}
      {bootBildAktiv && (
        <img
          src={bootImage}
          alt=""
          aria-hidden="true"
          onError={() => setBootBildFehler(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            pointerEvents: 'none',
            // Boot fertig -> weich zum Live-Slide durchreichen. Der Versuch
            // "Blende dünnt mit dem Fortschritt aus" (02.09.) ist bewusst
            // VERWORFEN: er gab den Blick auf die noch schwarze Buehne frei
            // (graues Zwischending statt Bild) — die Blende steht voll, bis
            // wirklich alles bereit ist.
            opacity: isReady ? 0 : 1,
            transition: 'opacity 0.6s ease-out',
          }}
        />
      )}
      {/* Intro-Vorhang — unabhängige Effekte über der ersten Slide (bzw. dem
          coverImage): Tint (Anzeigen) + Blur (Unscharf) via backdrop-filter. */}
      {(showCurtain || curtainBlur) && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background: showCurtain ? curtainGradient : 'transparent',
            // ECKEN IM HANDY-RAHMEN (Jans Fund 18.09.2026): ein backdrop-filter
            // wird vom abgerundeten overflow:hidden des Rahmens NICHT sauber
            // beschnitten — der unscharfe Vorhang ragte eckig ueber die runden
            // Rahmenecken. Die eigene Rundung beachtet der Filter dagegen
            // zuverlaessig. Der Rahmen nennt seinen Radius ueber die Variable
            // (ViewerChrome: .scrolly-shell-mask); ohne Rahmen / im Vollbild 0.
            borderRadius: 'var(--scrolly-vorhang-radius, 0px)',
            backdropFilter: curtainBlur ? 'blur(14px)' : undefined,
            WebkitBackdropFilter: curtainBlur ? 'blur(14px)' : undefined,
            pointerEvents: 'none',
          }}
        />
      )}
      {showCoverImage && isSentenceBloom && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '38%',
            zIndex: 2,
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 70%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />
      )}
      <div
        style={{
          maxWidth: basisMal(480 / 34),
          textAlign: pos.textAlign,
          position: 'relative',
          zIndex: 2,
        }}
      >
        {eyebrowText && (
          <div
            style={{
              fontFamily: textFamily,
              fontSize: `max(9px, ${basisMal(10.5 / 34)})`,
              fontWeight: 600,
              // W1 — Org-Pill-Farbe (nur emittiert wenn gesetzt) → Scrolly-Pink → Hex.
              color:
                textAkzent ??
                'var(--scrolly-brand-cover-accent, var(--scrolly-color-action, #F02873))',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: basisMal(10 / 34),
            }}
          >
            {eyebrowText}
          </div>
        )}

        {/* Leere Überschrift = KEIN Titel-Element (Jans Regel 30.08.2026:
            das Cover zeigt dann nur den START-Knopf) — ein leeres <h1>
            würde trotzdem Zeilenhöhe reservieren. */}
        {title ? (
            <h1
                style={{
                    fontFamily: headlineFamily,
                    fontSize: textBasisMal(headlineFaktor),
                    // EIGENE SCHRIFT WIE IM PRO-EDITOR (Jans Fund 04.09.2026:
                    // Roboto im Editor fett, in der Vorschau halbfett und eng
                    // gesetzt). Gewicht 500, negative Laufweite und Zeilenhoehe
                    // 0,95 sind die Gestaltung fuer die Standard-Serife. Waehlt
                    // das Scrolly unter Typografie eine eigene Schrift, zeigt
                    // der Editor sie mit Gewicht 800 und normaler Laufweite
                    // (StartScreenOverlay) — der Reader muss dasselbe Bild
                    // liefern, sonst baut man im Editor etwas, das so nie
                    // ausgeliefert wird. Zeilenhoehe bleibt in beiden 0,95
                    // (Jans Entscheidung 04.09.2026).
                    // Gewaehlter Schnitt (Schriftwahl, 06.10.2026) vor dem bisherigen Look
                    fontWeight: startTypography?.headlineWeight ?? (eigeneSchrift ? 800 : 500),
                    color: titleColor,
                    margin: 0,
                    // KEINE negative Laufweite mehr — auch nicht fuer die
                    // Standard-/Branding-Schrift des Template-Editors (Jan +
                    // Benedikt, 04.09.2026). Vorher -2/34 em, was bei vielen
                    // Schriften die Buchstaben ineinanderschob.
                    letterSpacing: 'normal',
                    lineHeight: 0.95,
                    textShadow: titleShadow,
                }}
            >
                {title}
            </h1>
        ) : null}

        {hasDescription && (
          <p
            style={{
              fontFamily: sublineFamily,
              fontWeight: startTypography?.sublineWeight,
              fontSize: `max(12px, ${textBasisMal((16 / 34) * sublineFaktor)})`,
              lineHeight: 1.5,
              color: descColor,
              marginTop: basisMal(16 / 34),
              marginBottom: 0,
            }}
          >
            {description}
          </p>
        )}

        {/* EIN Start-Knopf ueberall (Jans Vorgabe 26.08.2026): identische
            Optik mit Ladebalken + Dimmen in Reader, Previews und Editor —
            der fruehere "klassische" Zweig ohne Balken ist raus.
            animateLoading steuert nichts mehr, bleibt als Prop-Kompatibilitaet. */}
        {!isOgMode ? (
          <>
            {/* Ein hochgeladenes Bild ERSETZT den Knopf (gleiche Regel wie im
                Pro-Editor). Beim „Aufladen" ramp't die Deckkraft statt einer
                Füllung; bereit = volle Deckkraft + Settle. */}
            {showButton && startButtonImage && (
              <button
                type="button"
                className="cover-start-btn"
                onClick={startKlick}
                aria-label={
                  isReady
                    ? buttonLabel
                    : `${buttonLabel} — ${t('cover.loading', lang)} ${Math.round(anzeigeProgress * 100)}%`
                }
                data-testid="cover-start-button"
                style={{
                  marginTop: 24,
                  padding: 0,
                  cursor: isReady ? 'pointer' : 'progress',
                  background: 'none',
                  border: 'none',
                  // Waechst wie der Titel (Textbasis), nicht mit der
                  // Blockbreite — sonst wurde ein hochgeladener Knopf auf
                  // grossen Flaechen viel groesser als der Titel (Jans Fund
                  // 28.09.2026). 2,65 × Textbasis = die bisherige Groesse im
                  // Handy-Rahmen (86 px).
                  width: `clamp(80px, ${textBasisMal(2.65)}, 220px)`,
                  lineHeight: 0,
                  opacity: isReady ? 1 : 0.4 + 0.55 * anzeigeProgress,
                  transition: 'opacity 220ms ease-out',
                  animation: isReady
                    ? 'coverStartSettle 380ms cubic-bezier(0.2, 0.8, 0.2, 1)'
                    : undefined,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <img
                  src={startButtonImage}
                  alt={buttonLabel}
                  style={{ width: '100%', height: 'auto' }}
                />
              </button>
            )}

            {showButton && !startButtonImage && (
              <button
                type="button"
                className="cover-start-btn"
                onClick={startKlick}
                aria-label={
                  isReady
                    ? buttonLabel
                    : `${buttonLabel} — ${t('cover.loading', lang)} ${Math.round(anzeigeProgress * 100)}%`
                }
                data-testid="cover-start-button"
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  marginTop: 24,
                  padding: '12px 28px',
                  cursor: isReady ? 'pointer' : 'progress',
                  // Basis gedimmt-neutral; die Akzent-FÜLLUNG (span) steigt mit
                  // progress 0→100 % („Aufladen"), deckt bei voller Ladung 100 %.
                  background: 'rgba(120, 120, 130, 0.28)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  font: 'bold 14px var(--scrolly-font-ui, "DM Sans", -apple-system, system-ui, sans-serif)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  // Gestalteter Knopf (Pro-Editor) überlagert die Optik;
                  // das Aufladen läuft dann über die Deckkraft der Füllung.
                  ...stilStyles,
                  // Gestalteter Knopf MIT Fuellfarbe (Pro-Editor): Grundflaeche
                  // und Ladebalken hatten dieselbe Farbe — der Balken war da,
                  // aber unsichtbar (Jans Fund 20.09.2026: "im Pro-Editor kein
                  // Ladebalken"). Waehrend des Ladens deshalb dieselbe neutrale
                  // Grundflaeche wie beim Standard-Knopf; die Fuellfarbe waechst
                  // als Balken hinein und deckt bei voller Ladung alles.
                  ...(gestaltet && startButtonStyle?.fillColor && !isReady
                    ? { background: 'rgba(120, 120, 130, 0.28)' }
                    : null),
                  // Laden IMMER sichtbar machen — unabhaengig vom Knopf-Stil
                  // (bei hellen gestalteten Knoepfen war die Fuellung optisch
                  // unsichtbar und der Knopf wirkte faelschlich scharf).
                  opacity: isReady ? 1 : 0.55 + 0.4 * anzeigeProgress,
                  transition: 'opacity 220ms ease-out',
                  // Bereit: kurzer Settle. Kein Puls beim Laden, kein Glow-Ring.
                  animation: isReady
                    ? 'coverStartSettle 380ms cubic-bezier(0.2, 0.8, 0.2, 1)'
                    : undefined,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span
                  aria-hidden="true"
                  className="cover-start-fill"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${anzeigeProgress * 100}%`,
                    // Ladebalken UEBERALL sichtbar (Jans Vorgabe 26.08.):
                    // gestaltete Knoepfe nutzen ihre fillColor, sonst denselben
                    // Akzent wie der Standard-Knopf (das fast unsichtbare
                    // Weiss-18% war die Ursache der verpufften Klicks).
                    background:
                      (gestaltet && startButtonStyle?.fillColor) ||
                      // gestaltet OHNE Fuellung (Umriss-Knopf): Balken in der
                      // Akzentfarbe des Knopfs, halb durchsichtig — und bei voller
                      // Ladung wieder weg, sonst stuende der durchsichtige Knopf
                      // am Ende pink gefuellt da.
                      (gestaltet
                        ? `color-mix(in srgb, ${stilAkzent} 38%, transparent)`
                        : 'var(--scrolly-brand-cover-accent, var(--scrolly-color-action, #F02873))'),
                    opacity:
                      gestaltet && !startButtonStyle?.fillColor && isReady ? 0 : 1,
                    transition: 'width 240ms ease-out, opacity 320ms ease-out',
                    zIndex: 0,
                  }}
                />
                {!isReady && (
                  // CSS-only-Lebenszeichen (Jans Fund 02.09.: "90 % der Zeit
                  // tut sich nichts"): Der Schimmer laeuft als reine
                  // CSS-Animation schon aus dem Server-HTML — also auch
                  // waehrend JavaScript noch laedt und weder Rieseln noch
                  // echter Fortschritt existieren.
                  <span
                    aria-hidden="true"
                    className="cover-start-shimmer"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      overflow: 'hidden',
                      borderRadius: 'inherit',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: '60%',
                        background:
                          'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%)',
                        animation: 'coverShimmer 1.6s ease-in-out infinite',
                      }}
                    />
                  </span>
                )}
                <span style={{ position: 'relative', zIndex: 1 }}>
                  {buttonLabel}
                </span>
              </button>
            )}

            {/* Keyframes + Reduced-Motion (inline <style>, SSR-sicher). */}
            {!isOgMode && (
              <style>{`
                @keyframes coverStartSettle {
                  0% { transform: scale(1.07); }
                  60% { transform: scale(0.985); }
                  100% { transform: scale(1); }
                }
                @keyframes coverShimmer {
                  0% { transform: translateX(-70%); }
                  100% { transform: translateX(180%); }
                }
                @media (prefers-reduced-motion: reduce) {
                  .cover-start-btn { animation: none !important; }
                  .cover-start-fill { transition: none !important; }
                  .cover-start-shimmer { display: none !important; }
                }
              `}</style>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
